// src/services/httpClient.ts
import axios, { AxiosHeaders, InternalAxiosRequestConfig } from "axios";

function getCookie(name: string) {
  return document.cookie.split("; ").find(r => r.startsWith(name + "="))?.split("=")[1] ?? "";
}

const http = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "",
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ---- CSRF on writes ----
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = (config.method || "get").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const csrf = getCookie("csrf_token");
    if (csrf) (config.headers ??= new AxiosHeaders()).set("X-CSRF-Token", csrf);
  }
  return config;
});

// ---- 401 -> refresh -> retry (once) ----
let refreshing = false;
let waiters: Array<() => void> = [];

async function callRefresh() {
  try {
    await axios.post(
      (import.meta.env.VITE_API_URL || "") + "/auth/refresh",
      {},
      { withCredentials: true }
    );
  } catch {
    // ignore; caller will handle final 401
  }
}

http.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config as InternalAxiosRequestConfig & { __retried?: boolean };
    if (!cfg || error.response?.status !== 401 || cfg.__retried) {
      return Promise.reject(error);
    }

    if (refreshing) {
      await new Promise<void>((resolve) => waiters.push(resolve));
    } else {
      refreshing = true;
      await callRefresh();
      refreshing = false;
      waiters.splice(0).forEach(fn => fn());
    }

    cfg.__retried = true;
    return http(cfg);
  }
);

export default http;
export { getCookie };
